import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Store } from '@ngrx/store';
import { I18NextPipe, I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import * as moment from 'moment';
import { Moment } from 'moment';
import { ErrorMessage } from 'src/app/common/messages';
import { State } from 'src/app/dashboards/state';
import { gridItem } from 'src/app/models';
import { CommonService } from 'src/app/services/services/common.service';
import * as mainActions from 'src/app/state/app.actions';

@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'EN-UK' },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ],
    standalone: false
})
export class DatePickerComponent implements OnInit, OnChanges {
  @Input() public gridItem: gridItem;
  @Input() public dataAvailableFor: number;
  @Input() public firstDate: Date;
  @Input() public showTitle: boolean = true;

  @Output() public setPeriod = new EventEmitter<any>();

  rangeType = rangeType;
  selectedRange: rangeType = rangeType.day;
  value :number=-1;

  @Input() data:  any;
  @Output() dataChange = new EventEmitter<any>();

  selectedFrom: Moment;
  selectedTo: Moment;
  selectedFromTime: string;
  selectedToTime: String;
  maxFromDate: Moment = moment();
  maxToDate: Moment = moment();
  language: string;

  @ViewChild('calendarTo') private calendarTo: MatCalendar<Moment>;
  constructor(private i18nextPipe: I18NextPipe,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    private _adapter: DateAdapter<any>,
    @Inject(MAT_DATE_LOCALE) private _locale: string,
    private commonService:CommonService,
    private store: Store<State>) { }

  ngOnInit(): void {
    switch (this.i18NextService.language) {
      case "NO":
        this._locale = "nb";
        break;
      case "DK":
        this._locale = "da";
        break;
      case "SE":
        this._locale = "sv";
        break;
      case "EN":
        this._locale = "en-gb";
        break;
      default:
        this._locale = this.i18NextService.language.toLocaleLowerCase();
    }
    this._adapter.setLocale(this._locale);
    moment.locale(this._locale);

    if (this.gridItem?.setup?.period?.type == 1) {
      this.selectedRange = rangeType.custom;
      this.calcDatesFromRelative();
    } else {
      this.selectedRange = rangeType.day;
      this.selectedFromTime = "00:00";
      this.selectedToTime = "24:00";
      this.SetDates();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      let data = changes.data.currentValue;
      this.value = data.value;
      this.selectedRange = data.selectedRange;
      this.selectedFrom = data.selectedFrom;
      this.selectedTo = data.selectedTo;
      this.selectedFromTime = data.selectedFromTime;
      this.selectedToTime = data.selectedToTime;
    }
  }

  calcDatesFromRelative() {
    this.selectedFrom = this.commonService.calculateRelativeDate("from",this.gridItem);
    this.selectedTo = this.commonService.calculateRelativeDate("to",this.gridItem);
    this.selectedFromTime = this.gridItem.setup?.period?.fromTime || "00:00";
    if(this.gridItem.setup?.period?.toTime=="23:59"){
       this.selectedToTime = "24:00";
    }else{
       this.selectedToTime = this.gridItem.setup?.period?.toTime || "24:00";
    }
    this.CalcMaxToDate(this.calendarTo);
  }

  decrease() {
    this.value--;
    this.SetDates();
  }

  increase() {
    this.value++;
    if (this.value > 0) this.value = 0;
    this.SetDates();
  }

  selectType($event, type: rangeType) {
    let currentState = {
      selectedRange: this.selectedRange,
      value: this.value,
      selectedFrom: this.selectedFrom,
      selectedTo: this.selectedTo
    }


    if (type == rangeType.custom) {
      $event.stopPropagation();
    }
    this.selectedRange = type;
    this.value = -1;
    this.SetDates();

    if (this.dataAvailableFor > 0 && this.selectedTo.diff(this.selectedFrom, 'days') > this.dataAvailableFor) {
      this.selectedRange = currentState.selectedRange;
      this.value = currentState.value;
      this.selectedFrom = currentState.selectedFrom;
      this.selectedTo = currentState.selectedTo;
      this.SetDates();
      this.store.dispatch(
        mainActions.addMessage({
          message: new ErrorMessage(
            this.i18nextPipe.transform('messages:Period is too long'),
            this.i18nextPipe.transform("messages:The maximum time interval for this report is (in days)") + ": " + this.dataAvailableFor)
        }));
    }
  }

  CalcMaxToDate(calendarTo) {
    let maxDate = this.selectedFrom.clone().add(this.dataAvailableFor, 'days');

    if (maxDate > moment() || this.dataAvailableFor == 0) {
      maxDate = moment();
    }
    this.maxToDate = maxDate;
    if (this.selectedTo < this.selectedFrom) {
      this.selectedTo = this.selectedFrom;
      calendarTo.updateTodaysDate();
    }
    if (this.maxToDate < this.selectedTo) {
      this.selectedTo = this.maxToDate;
      calendarTo.updateTodaysDate();
    }

  }

  selectedText() {
    let d: Moment;
    switch (this.selectedRange) {
      case rangeType.day:
        d = moment().add(this.value, "d");
        if (this.value == 0) return `${this.i18nextPipe.transform('dashboard:Today')} (${d.format("L")})`;
        else if (this.value == -1) return `${this.i18nextPipe.transform('dashboard:Yesterday')} (${d.format("L")})`;
        else return d.format("dddd (L)")
      case rangeType.week:
        let text = "";
        d = moment().add(this.value, "w");
        if (this.value == 0) text = this.i18nextPipe.transform('dashboard:This week');
        else if (this.value == -1) text = this.i18nextPipe.transform('dashboard:Last week');
        else text = this.i18nextPipe.transform('dashboard:Week') + " " + d.isoWeek();
        return `${text} (${d.startOf("W").format("L")}-${d.endOf("W").format("L")})`;
      case rangeType.month:
        d = moment().add(this.value, "M");
        if (this.value == 0) return this.i18nextPipe.transform('dashboard:This month') + d.format(" (MMMM)");
        else if (this.value == -1) return this.i18nextPipe.transform('dashboard:Last month') + d.format(" (MMMM)");
        else return d.year() == moment().year() ? d.format("MMMM") : d.format("YYYY MMMM");
      default:
        return "";
    }
  }

  SetDates() {
    let from = moment().utcOffset(0);
    let to = moment().utcOffset(0);

    switch (this.selectedRange) {
      case rangeType.day:
        from.add(this.value, "d");
        to.add(this.value, "d");
        break;
      case rangeType.week:
        from.add(this.value, "w").startOf("W");
        to.add(this.value, "w").endOf("W");
        break;
      case rangeType.month:
        from.add(this.value, "M").startOf("M");
        to.add(this.value, "M").endOf("M");
        break;
      default:
        from = this.selectedFrom.clone();
        to = this.selectedTo.clone();
        break;
    }
    if (this.selectedRange == rangeType.custom) {
      let fromT = this.selectedFromTime.split(":").map(t => +t || 0);
      let toT = this.selectedToTime.split(":").map(t => +t || 0);
      for (let i = 0; i < 3; i++) {
        fromT[i] = fromT[i] || 0;
        toT[i] = toT[i] || 0;
      }
      if (fromT[0] == 24) { fromT = [23, 59, 59]; }
      if (toT[0] == 24) { toT = [23, 59, 59]; }
      from.set({ hour: fromT[0], minute: fromT[1], second: fromT[2], millisecond: 0 });
      to.set({ hour: toT[0], minute: toT[1], second: toT[2], millisecond: 0 });
    } else {
      from.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      to.set({ hour: 23, minute: 59, second: 59, millisecond: 0 });
      this.selectedFrom = from;
      this.selectedTo = to;
      this.selectedFromTime = "00:00";
      this.selectedToTime = "24:00";
    }

     this.setPeriod.emit({ type: 0, from: from.toDate(), to: to.toDate() });
     this.dataChange.emit({selectedRange:this.selectedRange,
      value:this.value,
      selectedFrom:this.selectedFrom,
      selectedTo:this.selectedTo,
      selectedFromTime:this.selectedFromTime,
      selectedToTime:this.selectedToTime
    });
  }
}

enum rangeType {
  day = 0,
  week = 1,
  month = 2,
  custom = 3
}
